/**
 * The DOM view. Builds the instrument panel once and exposes update methods and event
 * hooks; it holds no application state and touches no modules — main.ts/app.ts wire it to
 * the transport, recorder, store and telemetry. DOM lives here and in main.ts only.
 *
 * The far end is a passive responder, so the data screens (console/graph) carry almost no
 * controls: the deck is just the send box and Connect, and setup lives in sheets reached
 * from the header icons (Config, Sessions).
 */
import type { Stats, Series } from '../telemetry';
import type { CfgState } from '../cfg';
import { drawStrip } from './chart';
import { openConfigSheet, type ConfigState } from './config';
import type { RemoteState } from '../remote';

export type ConnState = 'disconnected' | 'connecting' | 'connected' | 'lost';
export type Theme = 'light' | 'dark';
export type Mode = 'console' | 'graph';
export type RowKind = 'rx' | 'tx' | 'evt';
export type SessionRow = { session: string; count: number; first?: string; last?: string; build?: string };

export type ViewHandlers = {
  onConnect(): void;
  onSend(text: string, src: 'macro' | 'typed'): void;
  onToggleTheme(): void;
  onToggleMode(mode: Mode): void;
  onOpenSessions(): void;
  onOpenConfig(): void;
  onApplyConfig(cfg: CfgState): void;
  onSetTofdelay(value: number): void;
  /** The session power ceiling — its own action, not an APPLY key (07 §9.1). */
  onSetPwrmax(value: number): void;
  /** The banner's one action — layer 2 of the fallback (06 §12.12). */
  onUndoConfig(): void;
  onToggleGps(enabled: boolean): void;
  onDownload(session: string): void;
  onDelete(session: string): void;
};

export type View = {
  appendRow(kind: RowKind, text: string): void;
  setConnection(state: ConnState): void;
  setGps(accuracy: number | null, ageMs: number | null): void;
  setRecordCount(n: number): void;
  setTheme(theme: Theme): void;
  setMode(mode: Mode): void;
  updateGraph(stats: Stats, series: Series): void;
  openSessions(sessions: SessionRow[], current: string): void;
  openConfig(state: ConfigState): void;
  setInputEnabled(enabled: boolean): void;
  clearTrace(): void;
  /**
   * Show or update the remote banner. **`null` removes it entirely** — a page with no relay must
   * have no extra pixels, which is the visible half of REQ-REM-1 and what keeps REQ-UI-6 true in
   * the normal case.
   */
  setRemote(state: RemoteState | null): void;
};

/** REQ-CON-4 — keep the most recent lines only. Exported so the test asserts this and not a copy. */
export const MAX_ROWS = 500;

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

function cell(label: string, id: string): { root: HTMLElement; val: HTMLElement } {
  const root = el('div', 'cell');
  root.append(el('span', 'lbl', label));
  const val = el('div', 'val');
  val.id = id;
  root.append(val);
  return { root, val };
}

/**
 * The banner's middle field, written to be **read out loud over a phone call** — that is layer 3
 * of the fallback (06 §12.12), and the person holding the phone may not be technical. So:
 * "linking", not "CONNECTING"; "quiet 62s", not "SILENT"; "340 held", not "queue depth 340".
 *
 * The host is here and the token is not, ever (REQ-REM-3) — which is the whole reason
 * `relayHost` exists.
 */
function remoteReading(s: RemoteState): string {
  const link =
    s.link === 'connecting'
      ? 'linking'
      : s.link === 'silent'
        ? `quiet ${Math.round(s.since / 1000)}s`
        : s.link; // 'linked' | 'stopped' already read correctly
  const parts = ['REMOTE', link, s.host];
  if (s.queued > 0) parts.push(`${s.queued} held`); // the laptop's copy is behind
  if (s.dropped > 0) parts.push(`${s.dropped} lost`); // the bound was hit; the phone's log is whole
  return parts.join(' · ');
}

function icon(glyph: string, label: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'icon', glyph);
  b.type = 'button';
  b.setAttribute('aria-label', label);
  b.addEventListener('click', onClick);
  return b;
}

export function createView(mount: HTMLElement, h: ViewHandlers): View {
  let mode: Mode = 'console';

  // --- status strip -------------------------------------------------------
  const strip = el('div', 'strip');
  const link = cell('Link', 'v-link');
  const dot = el('span', 'dot');
  const linkText = el('span', undefined, 'Offline');
  link.val.append(dot, linkText);
  link.root.setAttribute('role', 'button');
  link.root.addEventListener('click', () => h.onConnect());

  const gps = cell('GPS', 'v-gps');
  const gpsAcc = el('span', undefined, '—');
  const gpsAge = el('span', 'unit', '');
  gps.val.append(gpsAcc, gpsAge);

  const rec = cell('Rec', 'v-rec');
  const recVal = el('span', undefined, '0');
  rec.val.append(recVal);

  const icons = el('div', 'icons');
  const theme = icon('☾', 'Toggle theme', () => h.onToggleTheme());
  icons.append(
    icon('⚙', 'Config', () => h.onOpenConfig()),
    icon('▤', 'Sessions', () => h.onOpenSessions()),
    theme,
  );

  strip.append(link.root, gps.root, rec.root, icons);

  // --- remote banner ------------------------------------------------------
  // Built once and kept out of the DOM until there is a stream, so the offline page's markup is
  // byte-for-byte what it was before this feature existed.
  const banner = el('div', 'remote');
  const bannerText = el('span', 'remote-text', '');
  const undo = el('button', 'btn small', 'UNDO');
  undo.type = 'button';
  undo.addEventListener('click', () => h.onUndoConfig());
  banner.append(bannerText, undo);

  // --- mode tabs ----------------------------------------------------------
  const tabs = el('div', 'tabs');
  const tabConsole = el('button', 'tab active', 'Console');
  const tabGraph = el('button', 'tab', 'Graph');
  tabConsole.type = 'button';
  tabGraph.type = 'button';
  tabConsole.addEventListener('click', () => h.onToggleMode('console'));
  tabGraph.addEventListener('click', () => h.onToggleMode('graph'));
  tabs.append(tabConsole, tabGraph);

  // --- mid: console trace + graph panel -----------------------------------
  const mid = el('div', 'mid');

  const trace = el('div', 'trace');
  const empty = el('div', 'empty', 'Not connected. Tap Connect to choose the board.');
  trace.append(empty);
  let atBottom = true;
  trace.addEventListener('scroll', () => {
    atBottom = trace.scrollHeight - trace.scrollTop - trace.clientHeight < 24;
  });

  const graph = el('div', 'graph hidden');
  const counters = el('div', 'counters');
  const cPkts = cell('Pkts', 'c-pkts');
  const cPktsV = el('span', undefined, '0');
  cPkts.val.append(cPktsV);
  const cPer = cell('PER', 'c-per');
  const cPerV = el('span', undefined, '—');
  cPer.val.append(cPerV);
  const cRssi = cell('RSSI', 'c-rssi');
  const cRssiV = el('span', undefined, '—');
  const cRssiU = el('span', 'unit', '');
  cRssi.val.append(cRssiV, cRssiU);
  const cSnr = cell('SNR', 'c-snr');
  const cSnrV = el('span', undefined, '—');
  cSnr.val.append(cSnrV);
  // Only the four a field operator acts on: PER/RSSI/SNR (link quality) + Pkts (volume this
  // session). The raw seq= and rxtot= counters stay on every console rx line — they're the
  // working-out behind PER, not a survey readout, so they're off the graph header by choice.
  counters.append(cPkts.root, cPer.root, cRssi.root, cSnr.root);

  const rssiCanvas = el('canvas', 'chart');
  const snrCanvas = el('canvas', 'chart');
  graph.append(counters, rssiCanvas, snrCanvas);

  mid.append(trace, graph);

  // --- control deck (minimal) ---------------------------------------------
  const deck = el('div', 'deck');

  const sendRow = el('div', 'deck-row');
  const sendField = el('input', 'field');
  sendField.type = 'text';
  sendField.placeholder = 'type a command…';
  sendField.autocapitalize = 'off';
  sendField.autocomplete = 'off';
  sendField.spellcheck = false;
  const send = el('button', 'btn send', 'SEND');
  send.type = 'button';
  const doSend = () => {
    const t = sendField.value.trim();
    if (t) {
      h.onSend(t, 'typed');
      sendField.value = '';
    }
  };
  send.addEventListener('click', doSend);
  sendField.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') doSend();
  });
  sendRow.append(sendField, send);

  const connect = el('button', 'btn connect', 'CONNECT');
  connect.type = 'button';
  connect.addEventListener('click', () => h.onConnect());

  deck.append(sendRow, connect);

  mount.replaceChildren(strip, tabs, mid, deck);

  const inputs = [sendField, send];
  const setText = (node: HTMLElement, s: string) => (node.textContent = s);

  const view: View = {
    appendRow(kind, text) {
      empty.remove();
      const row = el('div', `row ${kind === 'rx' ? 'arrive' : kind}`);
      row.textContent = text;
      trace.append(row);
      while (trace.childElementCount > MAX_ROWS) trace.firstElementChild?.remove();
      if (atBottom) trace.scrollTop = trace.scrollHeight;
    },
    setConnection(state) {
      dot.className = 'dot';
      if (state === 'connected') {
        dot.classList.add('ok', 'live');
        linkText.textContent = 'Online';
        connect.classList.add('hidden');
      } else if (state === 'connecting') {
        dot.classList.add('warn');
        linkText.textContent = 'Link…';
        connect.classList.remove('hidden');
      } else {
        if (state === 'lost') dot.classList.add('err');
        linkText.textContent = state === 'lost' ? 'Lost' : 'Offline';
        connect.textContent = state === 'lost' ? 'RECONNECT' : 'CONNECT';
        connect.classList.remove('hidden');
      }
      view.setInputEnabled(state === 'connected');
    },
    setGps(accuracy, ageMs) {
      if (accuracy == null) {
        setText(gpsAcc, '—');
        setText(gpsAge, '');
        gps.root.classList.remove('stale');
        return;
      }
      setText(gpsAcc, `${accuracy.toFixed(0)}m`);
      const age = ageMs == null ? 0 : Math.round(ageMs / 1000);
      setText(gpsAge, `·${age}s`);
      gps.root.classList.toggle('stale', age > 10); // 7.6 s cadence — >10 s is stale
    },
    setRecordCount(n) {
      setText(recVal, String(n));
    },
    setTheme(t) {
      document.documentElement.dataset['theme'] = t;
      setText(theme, t === 'dark' ? '☀' : '☾');
    },
    setMode(m) {
      mode = m;
      tabConsole.classList.toggle('active', m === 'console');
      tabGraph.classList.toggle('active', m === 'graph');
      trace.classList.toggle('hidden', m !== 'console');
      graph.classList.toggle('hidden', m !== 'graph');
    },
    updateGraph(stats, series) {
      setText(cPktsV, String(stats.packets));
      setText(cPerV, stats.perPct == null ? '—' : `${stats.perPct}%`);
      if (stats.rssi) {
        setText(cRssiV, stats.rssi.last.toFixed(1));
        setText(cRssiU, `↓${stats.rssi.min.toFixed(0)}`);
      } else {
        setText(cRssiV, '—');
        setText(cRssiU, '');
      }
      setText(cSnrV, stats.snr ? stats.snr.last.toFixed(2) : '—');
      if (mode !== 'graph') return; // canvases have no size while hidden
      const css = getComputedStyle(document.documentElement);
      const grid = css.getPropertyValue('--hair').trim();
      const ink = css.getPropertyValue('--muted').trim();
      drawStrip(rssiCanvas, series.rssi, {
        min: -120, max: -40, color: css.getPropertyValue('--accent').trim(), grid, ink, label: 'RSSI', unit: 'dBm',
      });
      drawStrip(snrCanvas, series.snr, {
        min: -15, max: 10, color: css.getPropertyValue('--ok').trim(), grid, ink, label: 'SNR', unit: 'dB',
      });
    },
    openSessions(sessions, current) {
      openSheet(sessions, current, h);
    },
    openConfig(state) {
      openConfigSheet(state, {
        onApply: (cfg) => h.onApplyConfig(cfg),
        onSetTofdelay: (v) => h.onSetTofdelay(v),
        onSetPwrmax: (v) => h.onSetPwrmax(v),
        onSend: (t, src) => h.onSend(t, src),
        onToggleGps: (e) => h.onToggleGps(e),
      });
    },
    setRemote(state) {
      if (!state) {
        banner.remove();
        return;
      }
      bannerText.textContent = remoteReading(state);
      if (!banner.isConnected) strip.after(banner);
    },
    setInputEnabled(enabled) {
      for (const i of inputs) (i as HTMLButtonElement | HTMLInputElement).disabled = !enabled;
    },
    clearTrace() {
      trace.replaceChildren(empty);
    },
  };
  view.setInputEnabled(false);
  return view;
}

/** The session sheet — download / delete / recovery (REQ-LOG-4/5, REQ-UI-4). */
function openSheet(sessions: SessionRow[], current: string, h: ViewHandlers): void {
  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'sheet');
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  sheet.append(el('h2', undefined, 'Sessions'));
  if (sessions.length === 0) {
    sheet.append(el('div', 'empty', 'No stored sessions yet.'));
  }
  for (const s of [...sessions].reverse()) {
    const row = el('div', 'session');
    const info = el('div');
    const title = el('div', undefined, s.session.slice(0, 8) + (s.session === current ? ' · current' : ''));
    const meta = el('div', 'meta', `${s.count} rec${s.build ? ` · ${s.build}` : ''}${s.last ? ` · ${s.last.slice(11, 19)}` : ''}`);
    info.append(title, meta);
    const actions = el('div', 'session-actions');
    // Icons, not words. The row is session name + metadata + two actions on a 360px phone, and
    // "Download"/"Delete" spent about 150px of it on text the glyphs already say. `title` and
    // `aria-label` carry the meaning that the label used to — without them this would be two
    // unnamed buttons to a screen reader, and the delete one is destructive.
    const dl = el('button', 'btn small icon-btn', '⤓'); // ⤓ downwards arrow to bar
    dl.type = 'button';
    dl.title = 'Download';
    dl.setAttribute('aria-label', `Download session ${s.session.slice(0, 8)}`);
    dl.addEventListener('click', () => h.onDownload(s.session));
    const del = el('button', 'btn small warn icon-btn', '✕'); // ✕ multiplication x
    del.type = 'button';
    del.title = 'Delete';
    del.setAttribute('aria-label', `Delete session ${s.session.slice(0, 8)}`);
    del.addEventListener('click', () => {
      if (confirm(`Delete session ${s.session.slice(0, 8)} (${s.count} records)? This cannot be undone.`)) {
        h.onDelete(s.session);
        row.remove();
      }
    });
    actions.append(dl, del);
    row.append(info, actions);
    sheet.append(row);
  }
  const doneBtn = el('button', 'btn', 'Close');
  doneBtn.type = 'button';
  doneBtn.style.marginTop = '12px';
  doneBtn.style.width = '100%';
  doneBtn.addEventListener('click', close);
  sheet.append(doneBtn);

  backdrop.append(sheet);
  document.body.append(backdrop);
}
